import { jsx as _jsx, jsxs as _jsxs, Fragment as _Fragment } from "preact/jsx-runtime";
import { useEffect, useState, useRef, useCallback, Suspense, useContext, } from 'preact/compat';
import AwesomeDebouncePromise from 'awesome-debounce-promise';
import { FormProvider, useForm } from 'react-hook-form';
import API from '@widgets/shared/API/connection';
import LoadingSpinner from '@widgets/shared/components/LoadingSpinner';
import convertDataAttributes from '@widgets/shared/convertDataAttributes';
import { OptionsCTX, optionsDefaults } from '@siteSearch/context/Options';
import { useMemo } from 'react';
import SearchHeader from '@siteSearch/components/SearchHeader';
import SearchResult from './components/SearchResult';
import Styles from './styles';
const searchSiteDebounced = AwesomeDebouncePromise(API.searchSite, 500);
function Search() {
    const formRef = useRef(null);
    const { formAction, resultsTitle, initialSearchItems, perPage, initialTotal, initialMore, initialSearch, } = useContext(OptionsCTX);
    const initialItems = useMemo(() => {
        if (initialSearchItems.length > perPage) {
            return initialSearchItems.slice(0, perPage);
        }
        return initialSearchItems;
    }, [initialSearchItems, perPage]);
    console.log('Initial items', initialItems);
    const [items, setItems] = useState(initialItems);
    const [offset, setOffset] = useState(0);
    const [total, setTotal] = useState(initialTotal);
    const [complete, setComplete] = useState(!initialMore);
    const [lastSearch, setLastSearch] = useState(initialSearch);
    const [searchedTerm, setSearchedTerm] = useState(initialSearch);
    const [loading, setLoading] = useState(false);
    const [hasQueried, setHasQueried] = useState(initialItems.length > 0);
    const form = useForm({
        mode: 'onChange',
        defaultValues: {
            search: initialSearch,
        },
    });
    const { handleSubmit, watch, getFieldState, } = form;
    const { isDirty: searchIsDirty } = getFieldState('search');
    const search = watch('search');
    const handleSearch = useCallback((theSearch, theOffset) => {
        searchSiteDebounced(theSearch, theOffset)
            .then((resp) => {
            console.log('Resp: ', resp);
            setSearchedTerm(theSearch);
            setItems(theSearch === lastSearch ? [...items, ...resp.items] : resp.items); // append items if they're the same
            setTotal(resp.total);
            setComplete(!resp.more);
            setLastSearch(theSearch);
            setHasQueried(true);
            setLoading(false);
        });
    }, [lastSearch, items]);
    const handleSubmission = useCallback((ev) => {
        ev.preventDefault();
        setLoading(true);
        setOffset(0);
        setComplete(false);
        // console.log(`Searching for: ${search}`);
        handleSearch(search, 0);
    }, [search, offset, lastSearch, items]);
    const loadMore = useCallback(() => {
        const newOffset = offset + perPage;
        setOffset(newOffset);
        if (!complete) {
            setLoading(true);
            handleSearch(search, newOffset);
        }
    }, [offset, total, complete, items]);
    useEffect(() => {
        if (items.length > 0) {
            document.documentElement.classList.add('search-results-open');
        }
        else {
            document.documentElement.classList.remove('search-results-open');
        }
    }, [items]);
    return (_jsxs(Styles.Element, { children: [_jsx(FormProvider, { ...form, children: _jsx(SearchHeader, { handleSubmit: handleSubmission }) }), _jsxs("section", { className: "section section--search-results", children: [loading && (_jsx(Styles.LoadingWrapper, { children: _jsx(LoadingSpinner, { colourOverride: "#00346A" }) })), _jsxs("div", { className: "container container--small", children: [(items.length > 0) && (_jsxs("div", { className: "search-results", children: [_jsxs(_Fragment, { children: [_jsx("h1", { className: "h2", children: resultsTitle }), _jsx("div", { className: "search-results__header", children: _jsxs("span", { className: "title", children: ["\"", searchedTerm, "\""] }) })] }), _jsx("div", { className: "search-results__list", children: items.map((item) => (_jsx(SearchResult, { ...item }))) }), !complete && (_jsx("div", { className: "load-more", children: _jsx("button", { type: "button", className: "btn btn--outline", "aria-label": "Click to view all results", disabled: loading || complete, onClick: loadMore, children: "Load more" }) }))] })), (items.length === 0 && lastSearch !== '') && (_jsx("div", { className: "search-results", children: _jsxs("span", { className: "h2", children: ["No results found for \"", lastSearch, "\""] }) }))] })] })] }));
}
function SiteSearch(props) {
    const { dataset } = props;
    const formattedAttributes = convertDataAttributes(dataset ?? {});
    const componentConfig = useMemo(() => ({
        ...optionsDefaults,
        ...formattedAttributes,
    }), [formattedAttributes]);
    return (_jsx(Suspense, { fallback: LoadingSpinner, children: _jsx(OptionsCTX.Provider, { value: componentConfig, children: _jsx(Search, {}) }) }));
}
export default SiteSearch;
